import React from 'react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  Divider,
  Box,
  Text,
  Link,
  AbstractButton,
  Flex,
  Card,
  Icon,
  IconButton,
  StaticChip,
  useSnackbar,
} from 'pounceV2';
import useAuth from 'Hooks/useAuth';
import { UserInfo } from 'Components/utils/AuthContext';
import { getActorDisplayName, isCognitoSamlUser } from 'Helpers/utils';
import { PANTHER_CONFIG, PANTHER_SAML_USER_PREFIX } from 'Source/constants';
import { useGetRoleNameFromUserId } from 'Components/Navigation/graphql/getRoleNameFromUserId.generated';
import debounce from 'lodash/debounce';
import { useTheme, Theme } from '@mui/material';
import { SecondaryNavBaseProps } from 'Components/Navigation/types';
import { railCenterOffset } from 'Components/Navigation/constants';

// Constant across states so the avatar never resizes or shifts on expand/collapse.
const AVATAR_SIZE = 32;
// Profile row height, matched in both states so the footer stack never moves vertically.
const PROFILE_ROW_HEIGHT = 48;

const MenuItemBox: React.FC<{ theme: Theme; children: React.ReactNode }> = ({
  theme,
  children,
}) => {
  const hoverBg = theme.palette.componentTokens.selectMenuItems.color.item.background.row.hover;
  return (
    <Box
      mx={-2}
      sx={{
        transition: 'background-color 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        '&:hover': { backgroundColor: hoverBg },
        '&:focus': { backgroundColor: hoverBg },
      }}
    >
      {children}
    </Box>
  );
};

const DEFAULT_INITIALS = '??';

const getUserInitials = (userInfo?: UserInfo) => {
  if (userInfo?.givenName && userInfo?.familyName) {
    return `${userInfo.givenName[0]}${userInfo.familyName[0]}`;
  }

  if (userInfo?.givenName) {
    return userInfo.givenName.slice(0, 2);
  }

  if (userInfo?.familyName) {
    return userInfo.familyName.slice(0, 2);
  }

  if (userInfo?.email) {
    return userInfo.email.slice(0, 2);
  }

  if (userInfo?.id) {
    return userInfo.id.replace(`${PANTHER_SAML_USER_PREFIX}_`, '').slice(0, 2);
  }

  return DEFAULT_INITIALS;
};

const ProfileAvatar: React.FC<{ theme: Theme; isOpen: boolean; initials: string }> = ({
  theme,
  isOpen,
  initials,
}) => {
  const bg = theme.palette.componentTokens.avatar.colors.background.container;
  return (
    <Box
      width={AVATAR_SIZE}
      height={AVATAR_SIZE}
      display="flex"
      transition="background-color 0.1s linear"
      borderRadius="circle"
      justifyContent="center"
      alignItems="center"
      flexShrink={0}
      sx={{
        backgroundColor: isOpen ? bg.selected : bg.resting,
        '&:hover': { backgroundColor: bg.hover },
      }}
    >
      <Text variant="label/md" fontWeight="semibold">
        {initials}
      </Text>
    </Box>
  );
};

const ProfileInfoV3: React.FC<SecondaryNavBaseProps> = ({ isNavExpanded }) => {
  const theme = useTheme();
  const navColors = theme.palette.componentTokens.navigation.colors;
  const { userInfo, signOut } = useAuth();
  const { pushSnackbar } = useSnackbar();
  const { data: getUserDetails } = useGetRoleNameFromUserId({
    variables: {
      input: userInfo?.id,
    },
    skip: !userInfo?.id,
  });
  const roleName = getUserDetails?.userById?.role.name;

  const handleSignOut = React.useCallback(
    // `global: false` clears only this browser; `global: true` revokes everywhere
    // (incl. remote-MCP sessions on the shared Cognito pool) via GlobalSignOut.
    (global: boolean) => {
      signOut({
        global,
        onError: () => {
          pushSnackbar({
            variant: 'error',
            title: `Failed to Log Out`,
            description: 'We were unable to log you out at this time',
          });
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [signOut],
  );

  // One debounce per scope, not a shared one: a shared debounce keeps only the last
  // call's args, so "Log out all" → "Log out" would silently downgrade to a local sign-out.

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSignOutLocal = React.useCallback(
    debounce(() => handleSignOut(false), 350),
    [handleSignOut],
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSignOutAll = React.useCallback(
    debounce(() => handleSignOut(true), 350),
    [handleSignOut],
  );

  React.useEffect(() => {
    if (
      isCognitoSamlUser(userInfo) &&
      !userInfo.givenName &&
      !userInfo.familyName &&
      !userInfo.email
    ) {
      pushSnackbar({
        variant: 'warning',
        title: `SAML User Missing Attributes`,
        description:
          'Contact your administrator to update the attribute mappings in your SAML provider',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initials = getUserInitials(userInfo).toUpperCase();
  const displayName = getActorDisplayName(userInfo);

  return (
    <Popover>
      {({ isOpen }) => (
        <React.Fragment>
          {isNavExpanded ? (
            <Flex align="center" gap={3} width="100%" height={PROFILE_ROW_HEIGHT} px={3}>
              <ProfileAvatar theme={theme} isOpen={isOpen} initials={initials} />
              <Box flex="1 1 auto" minWidth={0} textAlign="left">
                <Text
                  variant="body/md"
                  fontWeight="medium"
                  truncated
                  color={navColors.text.userResting}
                >
                  {displayName}
                </Text>
                {!!userInfo?.email && (
                  <Text variant="body/sm" truncated color={navColors.text.static}>
                    {userInfo.email}
                  </Text>
                )}
              </Box>
              {/* The dots ARE the trigger — the row is a plain container, so this is a real
                  IconButton rather than a button nested inside another button. */}
              <PopoverTrigger
                as={IconButton}
                icon="options-dots-vertical"
                variant="ghost"
                size="medium"
                aria-label="Toggle User Menu"
                active={isOpen}
                flexShrink={0}
              />
            </Flex>
          ) : (
            // Left-offset (not center-justify) so the avatar sits on the rail's
            // center axis, aligned with the icons above and static across expand/collapse.
            <Flex
              align="center"
              width="100%"
              height={PROFILE_ROW_HEIGHT}
              pl={railCenterOffset(AVATAR_SIZE)}
            >
              <PopoverTrigger
                as={AbstractButton}
                aria-label="Toggle User Menu"
                borderRadius="circle"
                flexShrink={0}
              >
                <ProfileAvatar theme={theme} isOpen={isOpen} initials={initials} />
              </PopoverTrigger>
            </Flex>
          )}
          <PopoverContent alignment={isNavExpanded ? 'top-right' : 'right-top'}>
            <Card
              my={3}
              boxShadow={theme.shadows[23]}
              border={`1px solid ${theme.palette.componentTokens.selectMenuItems.color.item.stroke.container.resting}`}
              backgroundColor={navColors.background.profileMenu}
              minWidth={240}
              overflow="hidden"
            >
              <Box p={6}>
                <Text data-testid="user-display-name">{displayName}</Text>
                {!!userInfo && (
                  <Link size="large" external href={`mailto:${userInfo.email}`}>
                    {userInfo.email}
                  </Link>
                )}
                {!!roleName && (
                  <Box mt={2}>
                    <StaticChip
                      type="filled"
                      variant="subtle-attention"
                      size="sm"
                      data-testid="user-role-name"
                    >
                      {roleName}
                    </StaticChip>
                  </Box>
                )}
                <Divider mt={4} />
                <MenuItemBox theme={theme}>
                  <AbstractButton
                    p={2}
                    data-testid="log-out-all-sessions"
                    onClick={debouncedSignOutAll}
                  >
                    <Text>Log out all sessions</Text>
                  </AbstractButton>
                </MenuItemBox>
                <MenuItemBox theme={theme}>
                  <AbstractButton p={2} data-testid="log-out" onClick={debouncedSignOutLocal}>
                    <Text color={theme.palette.tokens.content.text.semantic.error.base}>
                      Log out
                    </Text>
                  </AbstractButton>
                </MenuItemBox>
              </Box>
              <Flex
                as="footer"
                justify="center"
                backgroundColor={theme.palette.tokens.background.neutral.canvas.base}
                p={3}
                mt={-2}
              >
                <Icon
                  type="panther"
                  size="small"
                  color={theme.palette.tokens.content.assets.on.color.light.dark}
                  mr={2}
                />
                <Text variant="body/sm" fontStyle="italic">
                  Version:&nbsp;<b>{PANTHER_CONFIG.PANTHER_VERSION}</b>
                </Text>
              </Flex>
            </Card>
          </PopoverContent>
        </React.Fragment>
      )}
    </Popover>
  );
};

export default React.memo(ProfileInfoV3);
